/**
 * FlagsInbox — Severity-sorted flag list with disposition workflow.
 *
 * Design law: docs/plans/2026-07-24-k3-black-orange-design-law.md
 * Uses StateChip for severity (the ONLY chip primitive allowed).
 */

import React, { useMemo, useState } from 'react';
import { StateChip } from '../shared/StateChip';
import { cn } from '../../lib/utils';

export type CaseFlagSeverity = 'blocker' | 'warning';
export type CaseFlagStatus = 'open' | 'acknowledged' | 'disputed' | 'waived';

const VALID_STATUSES: CaseFlagStatus[] = ['open', 'acknowledged', 'disputed', 'waived'];

export interface CaseFlag {
  id: string;
  severity: CaseFlagSeverity;
  title: string;
  detail: string;
  source: { kind: 'engine' | 'document' | 'agent'; ref: string };
  raised_at: string; // ISO-8601
  status: CaseFlagStatus;
  disposition_reason?: string; // populated server-side for non-open flags
}

export interface FlagsInboxProps {
  flags: CaseFlag[];
  onAcknowledge: (id: string) => void;
  onDispute: (id: string, reason: string) => void;
  onWaive: (id: string, reason: string) => void;
  onSourceClick: (flag: CaseFlag) => void; // caller wires SourceDrawer/PDF
}

type ActionMode = 'none' | 'dispute' | 'waive';

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isValidStatus(status: string): status is CaseFlagStatus {
  return VALID_STATUSES.includes(status as CaseFlagStatus);
}

function getStatusLabel(status: string): string {
  if (status === 'open') return 'Open';
  if (status === 'acknowledged') return 'Acknowledged';
  if (status === 'disputed') return 'Disputed';
  if (status === 'waived') return 'Waived';
  return 'Needs Human Review';
}

export function FlagsInbox({
  flags,
  onAcknowledge,
  onDispute,
  onWaive,
  onSourceClick,
}: FlagsInboxProps) {
  // Sort: blockers before warnings; within each tier preserve input order (stable)
  const sortedFlags = useMemo(() => {
    return [...flags].sort((a, b) => {
      const aScore = a.severity === 'blocker' ? 0 : 1;
      const bScore = b.severity === 'blocker' ? 0 : 1;
      return aScore - bScore;
    });
  }, [flags]);

  if (flags.length === 0) {
    return (
      <div className="text-sm text-[var(--color-text-secondary)]">
        No open flags — nothing blocking attestation.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortedFlags.map((flag) => (
        <FlagRow
          key={flag.id}
          flag={flag}
          onAcknowledge={onAcknowledge}
          onDispute={onDispute}
          onWaive={onWaive}
          onSourceClick={onSourceClick}
        />
      ))}
    </div>
  );
}

interface FlagRowProps {
  flag: CaseFlag;
  onAcknowledge: (id: string) => void;
  onDispute: (id: string, reason: string) => void;
  onWaive: (id: string, reason: string) => void;
  onSourceClick: (flag: CaseFlag) => void;
}

function FlagRow({ flag, onAcknowledge, onDispute, onWaive, onSourceClick }: FlagRowProps) {
  const [actionMode, setActionMode] = useState<ActionMode>('none');
  const [reason, setReason] = useState('');

  const isOpen = flag.status === 'open';

  // FAIL CLOSED: unknown status renders as 'needs-human'
  const effectiveStatus = isValidStatus(flag.status) ? flag.status : 'needs-human';

  const trimmedReason = reason.trim();
  const canConfirm = trimmedReason.length > 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    if (actionMode === 'dispute') {
      onDispute(flag.id, trimmedReason);
    } else if (actionMode === 'waive') {
      onWaive(flag.id, trimmedReason);
    }
    setActionMode('none');
    setReason('');
  };

  const handleCancel = () => {
    setActionMode('none');
    setReason('');
  };

  const startDispute = () => {
    setActionMode('dispute');
    setReason('');
  };

  const startWaive = () => {
    setActionMode('waive');
    setReason('');
  };

  return (
    <div
      data-testid={`flag-row-${flag.id}`}
      className={cn(
        'rounded-lg border p-4 transition-opacity',
        isOpen
          ? 'border-[var(--color-hairline)] bg-[var(--color-surface)]'
          : 'border-[var(--color-border-sub)] bg-[var(--color-surface)] opacity-60'
      )}
    >
      {/* Header: severity chip + title + timestamp */}
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <StateChip
            state={flag.severity === 'blocker' ? 'blocker' : 'warning'}
            text={flag.severity === 'blocker' ? 'Blocker' : 'Warning'}
            size="sm"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {flag.title}
            </div>
            <div
              className="shrink-0 text-xs text-[var(--color-text-secondary)]"
              data-testid={`timestamp-${flag.id}`}
            >
              {formatTimestamp(flag.raised_at)}
            </div>
          </div>

          {/* Detail */}
          <div className="mt-1 text-sm text-[var(--color-text-secondary)]">{flag.detail}</div>

          {/* Source chip */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => onSourceClick(flag)}
              data-testid={`source-chip-${flag.id}`}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                'border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] text-[var(--color-needs-human-text)]',
                'hover:bg-[var(--color-brand-tint-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
              )}
            >
              <span className="font-medium">{flag.source.kind}</span>
              <span className="text-[var(--color-text-dim)]">:</span>
              <span className="truncate max-w-[200px]" title={flag.source.ref}>
                {flag.source.ref}
              </span>
            </button>
          </div>

          {/* Non-open flags: show disposition info */}
          {!isOpen && (
            <div className="mt-3 text-xs text-[var(--color-text-secondary)]">
              <span className="font-medium">{getStatusLabel(effectiveStatus as string)}</span>
              {flag.disposition_reason ? (
                <span className="ml-2 italic">“{flag.disposition_reason}”</span>
              ) : null}
            </div>
          )}

          {/* Action buttons for open flags */}
          {isOpen && actionMode === 'none' && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => onAcknowledge(flag.id)}
                data-testid={`acknowledge-btn-${flag.id}`}
                className={cn(
                  'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium',
                  'bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
                )}
              >
                Acknowledge
              </button>
              <button
                type="button"
                onClick={startDispute}
                data-testid={`dispute-btn-${flag.id}`}
                className={cn(
                  'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium',
                  'border border-[var(--color-hairline)] bg-transparent text-[var(--color-text-primary)]',
                  'hover:bg-[var(--color-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
                )}
              >
                Dispute
              </button>
              <button
                type="button"
                onClick={startWaive}
                data-testid={`waive-btn-${flag.id}`}
                className={cn(
                  'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium',
                  'border border-[var(--color-hairline)] bg-transparent text-[var(--color-text-primary)]',
                  'hover:bg-[var(--color-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
                )}
              >
                Waive
              </button>
            </div>
          )}

          {/* Dispute/Waive inline form */}
          {isOpen && actionMode !== 'none' && (
            <div className="mt-3 space-y-2" data-testid={`action-form-${flag.id}`}>
              <label
                htmlFor={`reason-${flag.id}`}
                className="block text-xs font-medium text-[var(--color-text-secondary)]"
              >
                {actionMode === 'dispute' ? 'Reason for dispute' : 'Reason for waiver'}
              </label>
              <textarea
                id={`reason-${flag.id}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason..."
                data-testid={`reason-textarea-${flag.id}`}
                className={cn(
                  'w-full min-h-[80px] rounded-md border bg-[var(--color-bg)] px-3 py-2 text-sm',
                  'border-[var(--color-hairline)] text-[var(--color-text-primary)]',
                  'placeholder:text-[var(--color-text-dim)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
                )}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  data-testid={`confirm-btn-${flag.id}`}
                  className={cn(
                    'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium',
                    canConfirm
                      ? 'bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)]'
                      : 'bg-[var(--color-disabled-bg)] text-[var(--color-disabled-text)] cursor-not-allowed',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
                  )}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  data-testid={`cancel-btn-${flag.id}`}
                  className={cn(
                    'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium',
                    'border border-[var(--color-hairline)] bg-transparent text-[var(--color-text-primary)]',
                    'hover:bg-[var(--color-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]'
                  )}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FlagsInbox;
